import React, { useState, useEffect } from 'react';
import { ProjetosPageQuery_projetos_pedagogicos } from '../graphql/queries/__generated__/ProjetosPageQuery';
import { useProjetosLazyQuery } from '../graphql/queries/projetos.query';
import { useOfertasLazyQuery } from '../graphql/queries/ofertas.query';
import { useProfessoresLazyQuery } from '../graphql/queries/professores.query';
import { useDisciplinasLazyQuery } from '../graphql/queries/disciplinas.query';
import { useCadastrarProjetoMutation } from '../graphql/mutations/cadastrar_projeto.mutation';
import { useDeletarProjetoMutation } from '../graphql/mutations/deletar_projeto.mutation';
import { useEditarProjetoMutation } from '../graphql/mutations/editar_projeto.mutation';
import { FormProjeto, FormularioProjeto } from '../components/form_projeto_pedagogico';
import { FormList } from '../../shared/components/form_components/list';
import { ProjetoDisciplinasQuery_disciplinas } from '../graphql/queries/__generated__/ProjetoDisciplinasQuery';
import { ProjetoProfessoresQuery_professores } from '../graphql/queries/__generated__/ProjetoProfessoresQuery';
import { ProjetoOfertasQuery_ofertas } from '../graphql/queries/__generated__/ProjetoOfertasQuery';
import { SubContainer } from '../../shared/components/layout_components/sub_container/styles';
import { Container } from '../../shared/components/layout_components/container/styles';

const ProjetoPedagogicoPage: React.FC = () => {
  const [projeto, setProjeto] = useState<FormProjeto>();
  const [projetos, setProjetos] = useState<ProjetosPageQuery_projetos_pedagogicos[]>([]);
  const [ofertas, setOfertas] = useState<ProjetoOfertasQuery_ofertas[]>([]);
  const [professores, setProfessores] = useState<ProjetoProfessoresQuery_professores[]>([]);
  const [disciplinas, setDisciplinas] = useState<ProjetoDisciplinasQuery_disciplinas[]>([]);
  const [termoBusca, setTermoBusca] = useState('');
  const [paginaAtual, setPaginaAtual] = useState(1);
  const itens_por_pagina = 25;

  const [obter_projetos, { fetchMore: refresh_projetos, loading }] = useProjetosLazyQuery();
  const [obter_ofertas, { fetchMore: refresh_ofertas }] = useOfertasLazyQuery();
  const [obter_professores, { fetchMore: refresh_professores }] = useProfessoresLazyQuery();
  const [obter_disciplinas, { fetchMore: refresh_disciplinas }] = useDisciplinasLazyQuery();
  const [cadastar_projeto] = useCadastrarProjetoMutation();
  const [editar_projeto] = useEditarProjetoMutation();
  const [deletar_projeto] = useDeletarProjetoMutation();

  const on_submit = async (projeto: FormProjeto) => {
    if (!projeto.id_projeto_pedagogico) {
      const { data } = await cadastar_projeto({
        variables: {
          ...projeto,
        },
      });

      if (data) {
        setProjetos([...projetos, data.cadastrarProjetoPedagogico]);
        alert('Projeto cadastrado com sucesso!');
      }
    } else {
      const { data } = await editar_projeto({
        variables: {
          ...projeto,
          id_projeto_pedagogico: projeto.id_projeto_pedagogico,
        },
      });

      if (data) {
        const index = projetos.findIndex(
          (projeto) =>
            projeto.id_projeto_pedagogico === data.editarProjetoPedagogico.id_projeto_pedagogico
        );

        if (index !== -1) {
          const projetos_atualizados = [...projetos];
          projetos_atualizados[index] = data.editarProjetoPedagogico;
          setProjetos(projetos_atualizados);
        }

        alert('Projeto editado com sucesso!');
      }
    }

    setProjeto(undefined);
  };

  const on_edit = (projeto: ProjetosPageQuery_projetos_pedagogicos) => {
    setProjeto({
      id_projeto_pedagogico: projeto.id_projeto_pedagogico || 0,
      id_oferta: projeto.id_oferta,
      id_disciplina: projeto.id_disciplina,
      id_professor: projeto.id_professor || 0,
      ind_ordem: projeto.ind_ordem || 0,
      ind_semestre: projeto.ind_semestre || 0,
      num_carga_horaria_to: projeto.num_carga_horaria_to || 0,
      dsc_modalidade: projeto.dsc_modalidade || '',
      dsc_sigla_disciplina: projeto.dsc_sigla_disciplina || '',
    });
  };

  const on_delete = async (id_projeto_pedagogico: number) => {
    await deletar_projeto({ variables: { id_projeto_pedagogico } });
    setProjetos((projetos) =>
      projetos.filter((f) => f.id_projeto_pedagogico !== id_projeto_pedagogico)
    );
  };

  const on_cancel_edit = () => {
    setProjeto(undefined);
  };

  const obter_mais_dados = async (descricao = '', pagina = 1) => {
    const { data } = await refresh_projetos({
      variables: {
        descricao,
        pagina,
        itens_por_pagina,
      },
    });

    if (data && pagina > 1) {
      setProjetos([...projetos, ...data.projetos_pedagogicos]);
    } else {
      setProjetos(data.projetos_pedagogicos);
    }
  };

  const dados_dados_iniciais = async () => {
    const { data: data_projetos } = await obter_projetos({
      variables: {
        pagina: 1,
        itens_por_pagina,
      },
    });

    const { data: data_ofertas } = await obter_ofertas({
      variables: {
        pagina: 1,
        itens_por_pagina: undefined,
      },
    });

    const { data: data_professores } = await obter_professores({
      variables: {
        pagina: 1,
        itens_por_pagina: undefined,
      },
    });

    const { data: data_disciplinas } = await obter_disciplinas({
      variables: {
        pagina: 1,
        itens_por_pagina: undefined,
      },
    });

    if (data_projetos) {
      setProjetos(data_projetos.projetos_pedagogicos);
    }

    if (data_ofertas) {
      setOfertas(data_ofertas.ofertas);
    }

    if (data_professores) {
      setProfessores(data_professores.professores);
    }

    if (data_disciplinas) {
      setDisciplinas(data_disciplinas.disciplinas);
    }
  };

  const proxima_pagina = () => {
    setPaginaAtual((paginaAtual) => paginaAtual + 1);
  };

  useEffect(() => {
    obter_mais_dados(termoBusca);
  }, [termoBusca]);

  useEffect(() => {
    obter_mais_dados(termoBusca, paginaAtual);
  }, [paginaAtual]);

  useEffect(() => {
    document.title = 'SisIEC - Projetos pedagógicos';
    dados_dados_iniciais();
  }, []);

  return (
    <Container>
      <SubContainer flex={1}>
        <FormularioProjeto
          onSubmit={on_submit}
          onCancel={on_cancel_edit}
          projeto={projeto}
          ofertas={ofertas}
          professores={professores}
          disciplinas={disciplinas}
        />
      </SubContainer>

      <SubContainer flex={2}>
        <FormList
          list_name='Projetos'
          items={projetos.map((p) => ({
            data: p,
            id: p.id_projeto_pedagogico,
            label:
              (p.oferta.num_ano || '') +
              '/' +
              (p.oferta.num_semestre || '') +
              ' - ' +
              (p.disciplina.dsc_sigla || '') +
              ' - ' +
              (p.professor?.dsc_nome_professor || ''),
          }))}
          onSearch={setTermoBusca}
          onScroll={proxima_pagina}
          onEdit={on_edit}
          onDelete={on_delete}
          loading={loading}
        />
      </SubContainer>
    </Container>
  );
};

export { ProjetoPedagogicoPage };
